/**
 * FleetManager V3 - Theme Context
 * Manages light/dark/system theme preference
 * Syncs with database via auth/preferences endpoint
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../../api/auth.js';

const ThemeContext = createContext(null);

// Feature flag: set to true to re-enable dark mode toggle once all components support dark: classes
const DARK_MODE_ENABLED = false;

// Valid theme modes
const THEME_MODES = ['light', 'dark', 'system'];

/**
 * Determine effective theme based on mode and system preference
 */
function getEffectiveTheme(mode) {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
}

/**
 * Apply theme to document
 */
function applyTheme(effectiveTheme) {
  const root = document.documentElement;
  if (effectiveTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children }) {
  // Theme mode stored in DB: 'light', 'dark', or 'system'
  const [themeMode, setThemeMode] = useState('light');
  // Effective theme: 'light' or 'dark' (resolved from system if needed)
  const [effectiveTheme, setEffectiveTheme] = useState('light');
  const [isLoading, setIsLoading] = useState(true);

  // Load theme preference from API on mount
  useEffect(() => {
    let isMounted = true;

    async function loadPreferences() {
      // When dark mode is disabled, force light theme regardless of DB preference
      if (!DARK_MODE_ENABLED) {
        applyTheme('light');
        if (isMounted) setIsLoading(false);
        return;
      }

      try {
        const response = await authAPI.getPreferences();
        if (isMounted && response.ok && response.data) {
          const mode = response.data.theme_mode || 'light';
          setThemeMode(mode);
          const effective = getEffectiveTheme(mode);
          setEffectiveTheme(effective);
          applyTheme(effective);
        }
      } catch (err) {
        console.warn('Failed to load theme preferences:', err.message);
        // Default to light theme on error
        applyTheme('light');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadPreferences();
    return () => { isMounted = false; };
  }, []);

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (!DARK_MODE_ENABLED) return;
    if (themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      const effective = e.matches ? 'dark' : 'light';
      setEffectiveTheme(effective);
      applyTheme(effective);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Update theme mode and persist to DB
  const setTheme = useCallback(async (mode) => {
    if (!THEME_MODES.includes(mode)) {
      console.error('Invalid theme mode:', mode);
      return;
    }

    // Optimistically update UI
    setThemeMode(mode);
    const effective = getEffectiveTheme(mode);
    setEffectiveTheme(effective);
    applyTheme(effective);

    // Persist to database
    try {
      await authAPI.updatePreferences({ theme_mode: mode });
    } catch (err) {
      console.error('Failed to save theme preference:', err.message);
      // Don't revert - keep the UI change, it's just not persisted
    }
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newMode = effectiveTheme === 'light' ? 'dark' : 'light';
    setTheme(newMode);
  }, [effectiveTheme, setTheme]);

  // Cycle through modes: light -> dark -> system -> light
  const cycleTheme = useCallback(() => {
    const modes = ['light', 'dark', 'system'];
    const currentIndex = modes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setTheme(modes[nextIndex]);
  }, [themeMode, setTheme]);

  const value = React.useMemo(() => ({
    themeMode,        // The stored preference: 'light', 'dark', 'system'
    effectiveTheme,   // The actual applied theme: 'light' or 'dark'
    isLoading,
    setTheme,         // Set specific theme mode
    toggleTheme,      // Toggle between light/dark
    cycleTheme,       // Cycle through all modes
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light',
    isSystem: themeMode === 'system',
  }), [
    themeMode,
    effectiveTheme,
    isLoading,
    setTheme,
    toggleTheme,
    cycleTheme,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Optional hook that doesn't throw if outside provider
 */
export function useThemeOptional() {
  return useContext(ThemeContext);
}

export default ThemeContext;
